import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { incidentsApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, ShieldAlert, MapPin, User, Clock, CheckCircle2,
} from 'lucide-react';

const SEVERITY_CFG = {
  low: { color: '#16A34A', bg: '#DCFCE7' },
  medium: { color: '#CA8A04', bg: '#FEF3C7' },
  high: { color: '#DC2626', bg: '#FEE2E2' },
  critical: { color: '#7F1D1D', bg: '#FEE2E2' },
};

const STATUS_CFG = {
  open: { color: '#DC2626', bg: '#FEE2E2', label: 'Open' },
  acknowledged: { color: '#1A56DB', bg: '#DBEAFE', label: 'Acknowledged' },
  resolved: { color: '#16A34A', bg: '#DCFCE7', label: 'Resolved' },
  closed: { color: '#64748B', bg: '#F1F5F9', label: 'Closed' },
};

const TRIAGE_ROLES = ['super_admin', 'practice_admin', 'physician', 'nurse', 'care_coordinator'];

export default function IncidentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responseNotes, setResponseNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  const canTriage = TRIAGE_ROLES.includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await incidentsApi.get(id);
      setIncident(data);
      setResponseNotes(data.response_notes || '');
    } catch (err) {
      toast.error('Could not load incident');
      setIncident(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const setStatus = async (status) => {
    setUpdating(true);
    try {
      await incidentsApi.updateStatus(id, status, responseNotes);
      toast.success(`Marked ${status}`);
      await load();
    } catch (err) { toast.error('Update failed'); }
    finally { setUpdating(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0E7490]" /></div>;
  if (!incident) return <div className="p-8 text-center text-[#64748B]">Incident not found.</div>;

  const sev = SEVERITY_CFG[incident.severity] || SEVERITY_CFG.medium;
  const st = STATUS_CFG[incident.status] || STATUS_CFG.open;

  return (
    <div className="space-y-4 max-w-4xl" data-testid="incident-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/incidents')}><ArrowLeft className="w-4 h-4 mr-1" />Back to incidents</Button>

      <Card className="careiq-card">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <ShieldAlert className="w-6 h-6 text-[#DC2626]" />
                <h1 className="text-2xl font-bold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="incident-label">{incident.incident_label}</h1>
                <Badge style={{ color: sev.color, backgroundColor: sev.bg }} className="border-0 text-xs font-bold">{incident.severity.toUpperCase()}</Badge>
                <Badge style={{ color: st.color, backgroundColor: st.bg }} className="border-0 text-xs">{st.label}</Badge>
              </div>
              <div className="text-sm text-[#64748B] flex flex-wrap gap-3">
                {incident.patient_name && <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{incident.patient_name}</span>}
                {incident.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{incident.location}</span>}
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Occurred {new Date(incident.occurred_at).toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="border-t border-[#E2E8F0] pt-4">
            <div className="text-xs font-bold uppercase tracking-wider text-[#64748B] mb-2">Reporter</div>
            <div className="text-sm text-[#0A2342]">{incident.reporter_name} <span className="text-[#64748B]">({incident.reporter_role})</span></div>
            <div className="text-xs text-[#94A3B8] mt-0.5">Reported {new Date(incident.reported_at).toLocaleString()}</div>
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#64748B] mb-2">Narrative</div>
            <p className="text-sm text-[#0A2342] whitespace-pre-wrap" data-testid="incident-narrative">{incident.narrative}</p>
          </div>

          {incident.extras && Object.keys(incident.extras).length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#64748B] mb-2">Additional details</div>
              <dl className="grid grid-cols-2 gap-2 text-sm">
                {Object.entries(incident.extras).map(([k, v]) => (
                  <React.Fragment key={k}>
                    <dt className="text-[#64748B] capitalize">{k.replace(/_/g, ' ')}</dt>
                    <dd className="text-[#0A2342] font-medium">{v || '—'}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          )}

          {(incident.photos || []).length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#64748B] mb-2">Photos</div>
              <div className="flex gap-2 flex-wrap" data-testid="incident-photos">
                {incident.photos.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt="" className="w-32 h-32 object-cover rounded-lg border border-[#E2E8F0]" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {incident.acknowledged_at && (
            <div className="bg-[#DBEAFE] rounded-lg p-3 text-sm">
              <div className="font-semibold text-[#1E40AF] flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Acknowledged</div>
              <div className="text-[#1E40AF] text-xs mt-0.5">by {incident.acknowledged_by_name} on {new Date(incident.acknowledged_at).toLocaleString()}</div>
            </div>
          )}
          {incident.resolved_at && (
            <div className="bg-[#DCFCE7] rounded-lg p-3 text-sm">
              <div className="font-semibold text-[#166534] flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Resolved {new Date(incident.resolved_at).toLocaleString()}</div>
              {incident.response_notes && <div className="text-[#166534] text-xs mt-1 whitespace-pre-wrap">{incident.response_notes}</div>}
            </div>
          )}

          {canTriage && incident.status !== 'closed' && (
            <div className="border-t border-[#E2E8F0] pt-4 space-y-3" data-testid="triage-panel">
              <div className="text-xs font-bold uppercase tracking-wider text-[#64748B]">Triage</div>
              <Textarea rows={3} value={responseNotes} onChange={(e) => setResponseNotes(e.target.value)} placeholder="Response notes (visible to the reporter)…" data-testid="triage-notes-input" />
              <div className="flex gap-2 flex-wrap">
                {incident.status === 'open' && (
                  <Button onClick={() => setStatus('acknowledged')} disabled={updating} variant="outline" data-testid="ack-btn">
                    {updating && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Acknowledge
                  </Button>
                )}
                {incident.status !== 'resolved' && (
                  <Button onClick={() => setStatus('resolved')} disabled={updating} className="bg-[#16A34A] hover:bg-[#166534] text-white" data-testid="resolve-btn">
                    {updating && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Resolve
                  </Button>
                )}
                <Button onClick={() => setStatus('closed')} disabled={updating} variant="outline" data-testid="close-btn">Close</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
